import { ENGAGEMENT_ZONE_DEFAULT_TRANSONIC_SEVERITY_PERCENT } from "./engagement-zone.js";

const dragFunctions = ["G1", "G2", "G5", "G6", "G7", "G8", "GL", "GI"]
  .map((label, index) => ({ value: String(index + 1), label }));

const outputModes = [
  { value: "angular", label: "Angular" },
  { value: "linear", label: "Linear" },
];

const trajectorySourceControlledResultUnits = Object.freeze([
  "Elevation", "Azimuth", "Elevation Correction", "Azimuth Correction",
  "Drop", "Windage", "Spin Drift", "Coriolis Windage", "Lead",
]);

const TRAJECTORY_MAX_RANGE_YARDS = 3000 / 0.9144;
const TRAJECTORY_MAX_MINIMUM_RANGE_YARDS = TRAJECTORY_MAX_RANGE_YARDS - 100;

const number = (name, label, value, options = {}) => ({
  type: "number", name, label, value: String(value), ...options,
});

const numberList = (name, label, value, options = {}) => ({
  type: "number-list", name, label, value: String(value), ...options,
});

const velocityBcList = (name, label, value, options = {}) => ({
  type: "velocity-bc-list", name, label, value: String(value), ...options,
});

const temperatureVelocityList = (name, label, value, options = {}) => ({
  type: "temperature-velocity-list", name, label, value: String(value), ...options,
});

const select = (name, label, value, options, extra = {}) => ({
  type: "select", name, label, value: String(value), options, ...extra,
});

const checkbox = (name, label, checked = false, options = {}) => ({
  type: "checkbox", name, label, checked, ...options,
});

const hidden = (name, value) => ({ type: "hidden", name, value: String(value) });

const helpByValue = (name, values) => ({ name, values });

const velocityBcModeHelp = helpByValue("bcm", {
  1: "Uses the entered ballistic coefficient unchanged at every velocity.",
  2: "Estimates how BC changes with Mach from the entered bullet geometry. The entered BC anchors the curve at the standard-atmosphere reference muzzle velocity.",
  3: "Uses the custom velocity thresholds below. Each new BC takes effect when the projectile slows to or below its threshold.",
});

const ballisticCoefficientHelp = helpByValue("bcm", {
  1: "The constant ballistic coefficient used at every velocity.",
  2: "The reference ballistic coefficient used to scale the geometry-estimated BC curve at the standard-atmosphere reference muzzle velocity.",
  3: "The ballistic coefficient used above the highest custom velocity threshold.",
});

const shotAngleHelp = "The sight line's inclination relative to horizontal: positive is uphill and negative is downhill. The Trajectory and Range Table engines use the shot inclination angle to update local temperature, pressure, density, and speed of sound as the bullet changes height.";
const zeroShiftElevationHelp = "At the selected zero range, the projectile's point of impact should be off by this amount. Negative values indicate low. Positive values indicate high.";
const zeroShiftWindageHelp = "At the selected zero range, the projectile's point of impact should be off by this amount. Negative values indicate left. Positive values indicate right.";
const dialedElevationHelp = "An elevation adjustment applied after the gun's zero has been established. Enter the post-zero correction dialed for this shot—not the turret position or adjustment used to establish zero. The automatically calculated elevation correction takes precedence for the solution.";
const dialedWindageHelp = "A windage adjustment applied after the gun's zero has been established. Enter the post-zero horizontal correction dialed for this shot—not the turret position or adjustment used to establish zero. The automatically calculated azimuth correction takes precedence for the solution.";
const targetSpeedHelp = "The target's apparent speed used to estimate lateral lead. This approximation does not account for the target moving closer or farther away and does not change the calculated range, time of flight, drop, or velocity.";
const targetDirectionHelp = "The apparent direction of travel, at the given target speed, for the target. 0 is away, 180 is directly towards the shooter, 90 is directly to the right from the perspective of the shooter, and 270 is directly to the left from the perspective of the shooter. This approximation calculates lateral lead only; away or toward motion does not change the calculated range, time of flight, drop, or velocity.";
const windDirectionHelp = "The direction the wind is coming from, measured clockwise from true north. The firing or target bearing converts this absolute direction into the downrange and cross-range components used by the trajectory engine.";
const targetBearingHelp = "The bearing from shooter to target, measured clockwise from true north. It orients the wind vector and also serves as firing azimuth when an explicit latitude enables Coriolis calculations.";
const engagementUncertaintyHelp = "A symmetric ±95% uncertainty interval. Engagement Zone converts this value to a normal distribution with a standard deviation equal to half the entered uncertainty.";
const mvTemperatureTableHelp = "Add each ammunition temperature where you know the measured muzzle velocity. An exact temperature uses its measured velocity. A temperature between two entries uses the straight-line change between them. Outside the measured temperatures, the calculator uses MV temperature sensitivity from the nearest entry.";
const mvTemperatureSensitivityHelp = "Fallback used only when the ammunition temperature is outside the measured MV-Temp table. Enter how much muzzle velocity changes for each degree of ammunition temperature; the calculator applies it from the nearest known entry.";
const ICAO_PRESSURE_INHG = 1013.25 / 33.86389;

const standardAtmospherePresets = Object.freeze([
  {
    id: "icao",
    label: "ICAO",
    details: "59 °F · 29.92 inHg · 0% RH · sea level",
    values: { tp: 59, pr: ICAO_PRESSURE_INHG, hu: 0, al: 0, alc: false },
  },
  {
    id: "asm",
    label: "ASM",
    details: "59 °F · 29.53 inHg · 78% RH · sea level",
    values: { tp: 59, pr: 29.53, hu: 78, al: 0, alc: false },
  },
]);

const rangeTableAtmospherePresets = Object.freeze(standardAtmospherePresets.map((preset) => ({
  ...preset,
  details: `${preset.values.pr} inHg · ${preset.values.hu}% RH · sea level · reference and series temperatures unchanged`,
  values: Object.fromEntries(Object.entries(preset.values)
    .filter(([name]) => ["pr", "hu"].includes(name))),
})));

const velocityBandFields = () => [
  select("bcm", "Velocity thresholds and BCs", 1, [
    { value: "1", label: "Fixed BC (no calculation)" },
    { value: "2", label: "Estimate from bullet geometry" },
    { value: "3", label: "Custom bands" },
  ], {
    wide: true,
    help: velocityBcModeHelp,
  }),
  velocityBcList("bcb", "Custom velocity thresholds and BCs", "", {
    unit: "ft/s",
    wide: true,
    placeholder: "2600 -> 0.470\n2100 -> 0.430\n1600 -> 0.390",
    help: "Enter one velocity -> BC pair per line. The entered BC applies above the highest threshold; at or below each threshold, its BC takes effect as the projectile slows. These are flight-speed bands, not muzzle-velocity choices. Bands are sorted automatically.",
    showWhen: { name: "bcm", value: "3" },
  }),
];

const geometryEstimateCondition = Object.freeze({ name: "bcm", value: "2" });

const bcEstimateGeometry = ({ supportsSpinDrift = false } = {}) => ({
  title: "Bullet geometry",
  icon: "bi-bezier2",
  ...(supportsSpinDrift ? {
    showWhenAny: [
      geometryEstimateCondition,
      { name: "sdc", checked: true },
    ],
  } : { showWhen: geometryEstimateCondition }),
  fields: [
    number("cl", "Bullet caliber", 0.308, { min: 0.1, max: 2, step: 0.001, unit: "in" }),
    number("bl", "Total bullet length", 1.464, { min: 0.1, max: 10, step: 0.001, unit: "in" }),
    number("nsl", "Nose length", 0.740, {
      min: 0.1, max: 6, step: 0.001, unit: "in", showWhen: geometryEstimateCondition,
    }),
    number("dmp", "Meplat diameter", 0.062, {
      min: 0, max: 1, step: 0.001, unit: "in", showWhen: geometryEstimateCondition,
    }),
    number("rad", "Nose radius", 15, {
      min: 1, max: 1000, step: 0.1, unit: "cal", showWhen: geometryEstimateCondition,
    }),
    select("nst", "Nose type", 1, [
      { value: "1", label: "Secant" },
      { value: "2", label: "Tangent" },
      { value: "3", label: "Conical" },
    ], { showWhen: geometryEstimateCondition }),
    number("btl", "Boattail length", 0.180, {
      min: 0, max: 2, step: 0.001, unit: "in", showWhen: geometryEstimateCondition,
    }),
    number("dba", "Base diameter", 0.251, {
      min: 0.01, max: 1, step: 0.001, unit: "in", showWhen: geometryEstimateCondition,
    }),
    number("dbd", "Bearing-band diameter", 0.308, {
      min: 0.01, max: 1.2, step: 0.001, unit: "in", showWhen: geometryEstimateCondition,
    }),
  ],
});

const mvTemperatureSensitivity = () => number("mvs", "MV temperature sensitivity", 1, {
  min: -20, max: 20, step: 0.01, unit: "ft/s/°F",
  advanced: true,
  help: mvTemperatureSensitivityHelp,
});

const atmosphere = ({ supportsMvTemperature = false, ...options } = {}) => ({
  title: "Atmosphere",
  icon: "bi-cloud-sun",
  inputPresets: {
    label: "Load sea-level atmosphere",
    help: "ICAO (International Civil Aviation Organization) is the modern dry-air sea-level standard: 59 °F, 29.92 inHg, and 0% relative humidity. ASM (Army Standard Metro) is the older ballistic standard: 59 °F, 29.53 inHg, and 78% relative humidity. A preset also sets altitude to sea level and turns off automatic standard-at-altitude conditions. These buttons fill ambient inputs; they do not convert the entered ballistic coefficient between reference standards.",
    options: standardAtmospherePresets,
  },
  ...options,
  fields: [
    number("tp", "Temperature", 59, { min: -40, max: 140, step: 0.1, unit: "°F" }),
    ...(supportsMvTemperature ? [mvTemperatureSensitivity()] : []),
    number("pr", "Barometric pressure", ICAO_PRESSURE_INHG, { min: 15, max: 40, step: 0.01, unit: "in Hg" }),
    number("hu", "Relative humidity", 0, { min: 0, max: 100, step: 0.1, unit: "%" }),
    checkbox("alc", "Use standard atmospheric conditions at altitude", false, {
      help: "Replaces the entered temperature, pressure, and humidity with ICAO standard-atmosphere values at the selected altitude.",
    }),
    number("al", "Altitude", 0, {
      min: -1000, max: 15000, step: 1, unit: "ft",
      help: "Used only when standard atmospheric conditions at altitude are enabled. Otherwise the entered temperature and barometric pressure determine air density directly.",
      showWhenAny: [{ name: "alc", checked: true }],
    }),
  ],
});

const trajectoryRange = () => ({
  title: "Range and output",
  icon: "bi-table",
  fields: [
    number("rn", "Minimum range", 0, {
      min: 0, max: TRAJECTORY_MAX_MINIMUM_RANGE_YARDS, step: "any", unit: "yd",
      advanced: true,
    }),
    number("rx", "Maximum range", TRAJECTORY_MAX_RANGE_YARDS, { min: 100, max: TRAJECTORY_MAX_RANGE_YARDS, step: "any", unit: "yd" }),
    number("ri", "Range increment", 1, { min: 1, max: 500, step: "any", unit: "yd" }),
    hidden("elc", "on"),
    hidden("azc", "on"),
    hidden("un", 3),
    select("outputMode", "Output", "angular", outputModes, { bottomBar: true }),
  ],
});

const trajectoryCorrections = ({ spinDrift = false, advanced = false } = {}) => ({
  title: "Earth rotation and spin",
  icon: "bi-globe-americas",
  advanced,
  fields: [
    checkbox("sdc", "Apply spin drift", spinDrift),
    number("tw", "Barrel twist", 10, { min: 2, max: 100, step: 0.1, unit: "in/turn" }),
    select("td", "Twist direction", 1, [
      { value: "1", label: "Right" },
      { value: "-1", label: "Left" },
    ]),
    checkbox("coc", "Apply Coriolis correction"),
    number("lat", "Latitude", 0, { min: -90, max: 90, step: 0.01, unit: "°" }),
    number("faz", "Firing azimuth (true north)", 0, {
      min: 0, max: 360, step: 0.1, unit: "°",
    }),
  ],
});

const lateralLead = ({ advanced = false } = {}) => ({
  title: "Lateral lead",
  icon: "bi-arrow-left-right",
  advanced,
  fields: [
    number("sp", "Target speed", 0, {
      min: 0, max: 100, step: 0.1, unit: "mph", help: targetSpeedHelp,
    }),
    number("sa", "Target direction of travel", 0, {
      min: 0, max: 360, step: 1, unit: "°",
      help: targetDirectionHelp,
    }),
  ],
});

const gunProjectileFields = () => [
  temperatureVelocityList("mvtb", "MV-Temp table", "59:3000", {
    wide: true, required: true, maxRows: 32,
    help: mvTemperatureTableHelp,
  }),
  number("xc", "Chronograph distance", 0, {
    min: 0, max: 100, step: 0.1, unit: "ft", required: true,
    help: "Distance from the muzzle at which every velocity in the MV-Temp table was measured. Enter zero when the table contains true muzzle velocities.",
  }),
  number("bc", "Ballistic coefficient", 0.5, {
    min: 0.05, max: 2, step: 0.001, unit: "G1", unitField: "df",
    help: ballisticCoefficientHelp, required: true,
  }),
  hidden("df", 1),
  number("wt", "Bullet weight", 220, {
    min: 10, max: 5000, step: 0.1, unit: "gr", required: true,
  }),
  ...velocityBandFields(),
];

const gunGeometrySection = {
  title: "Projectile geometry",
  icon: "bi-bezier2",
  fields: [
    number("cl", "Bullet caliber", 0.308, {
      min: 0.1, max: 2, step: 0.001, unit: "in", required: true,
      help: "Required with bullet length because spin drift is always calculated for a loaded gun.",
    }),
    number("bl", "Total bullet length", 1.464, {
      min: 0.1, max: 10, step: 0.001, unit: "in", required: true,
      help: "Required with caliber because spin drift is always calculated for a loaded gun.",
    }),
    number("nsl", "Nose length", 0.740, {
      min: 0.1, max: 6, step: 0.001, unit: "in", required: true,
      showWhen: geometryEstimateCondition,
    }),
    number("dmp", "Meplat diameter", 0.062, {
      min: 0, max: 1, step: 0.001, unit: "in", required: true,
      showWhen: geometryEstimateCondition,
    }),
    number("rad", "Nose radius", 15, {
      min: 1, max: 1000, step: 0.1, unit: "cal", required: true,
      showWhen: geometryEstimateCondition,
    }),
    select("nst", "Nose type", 1, [
      { value: "1", label: "Secant" },
      { value: "2", label: "Tangent" },
      { value: "3", label: "Conical" },
    ], { showWhen: geometryEstimateCondition, required: true }),
    number("btl", "Boattail length", 0.180, {
      min: 0, max: 2, step: 0.001, unit: "in", required: true,
      showWhen: geometryEstimateCondition,
    }),
    number("dba", "Base diameter", 0.251, {
      min: 0.01, max: 1, step: 0.001, unit: "in", required: true,
      showWhen: geometryEstimateCondition,
    }),
    number("dbd", "Bearing-band diameter", 0.308, {
      min: 0.01, max: 1.2, step: 0.001, unit: "in", required: true,
      showWhen: geometryEstimateCondition,
    }),
  ],
};

export const gunSections = Object.freeze([
  {
    title: "Projectile and ammunition",
    icon: "bi-bullseye",
    fields: gunProjectileFields(),
  },
  gunGeometrySection,
  {
    title: "Sight and zero",
    icon: "bi-crosshair",
    fields: [
      number("sh", "Sight height", 1.5, {
        min: 0, max: 100, step: 0.01, unit: "in", required: true,
      }),
      number("so", "Sight offset", 0, {
        min: -100, max: 100, step: 0.01, unit: "in", required: true,
      }),
      number("rz", "Zero range", 100, {
        min: 10, max: TRAJECTORY_MAX_RANGE_YARDS, step: "any", unit: "yd", required: true,
      }),
      number("zy", "Zero shift elevation", 0, {
        min: -100, max: 100, step: 0.01, unit: "in", required: true,
        help: zeroShiftElevationHelp,
      }),
      number("zz", "Zero shift windage", 0, {
        min: -100, max: 100, step: 0.01, unit: "in", required: true,
        help: zeroShiftWindageHelp,
      }),
    ],
  },
  {
    title: "Required spin drift",
    icon: "bi-arrow-repeat",
    fields: [
      number("tw", "Barrel twist", 10, {
        min: 2, max: 100, step: 0.1, unit: "in/turn", required: true,
        help: "Rifling twist rate used with projectile geometry to calculate gyroscopic stability and spin drift.",
      }),
      select("td", "Twist direction", 1, [
        { value: "1", label: "Right" },
        { value: "-1", label: "Left" },
      ], {
        required: true,
        help: "Spin drift follows the twist direction: right for right-hand twist and left for left-hand twist.",
      }),
    ],
  },
]);

export const gunEditor = Object.freeze({
  slug: "gun",
  title: "Loaded Gun",
  sections: gunSections,
});

export const gunFieldNames = Object.freeze(gunSections
  .flatMap((section) => section.fields.map((field) => field.name)));

const trajectoryGunParameters = Object.freeze([...gunFieldNames, "sdc"]);
const pointBlankGunParameters = Object.freeze([
  "mvtb", "xc", "bc", "df", "wt", "bcm", "bcb",
  "cl", "bl", "nsl", "dmp", "rad", "nst", "btl", "dba", "dbd", "sh",
]);
const maximumDistanceGunParameters = Object.freeze([
  "mvtb", "xc", "bc", "df", "wt", "bcm", "bcb",
  "cl", "bl", "nsl", "dmp", "rad", "nst", "btl", "dba", "dbd",
]);

const derivedMuzzleVelocityParameter = Object.freeze(["mv"]);
const trajectoryPopulateGunParameters = Object.freeze([
  "mvtb", "xc", "bc", "df", "wt", "bcm", "bcb",
  "cl", "bl", "nsl", "dmp", "rad", "nst", "btl", "dba", "dbd",
  "sh", "so", "rz", "zy", "zz", "tw", "td",
]);

const solutionSections = [
  {
    title: "Target",
    icon: "bi-crosshair2",
    fields: [
      number("tg", "Target range", 1000, {
        min: 100, max: TRAJECTORY_MAX_RANGE_YARDS, step: "any", unit: "yd",
      }),
      number("tb", "Target bearing", 0, {
        min: 0, max: 360, step: 0.1, unit: "°", help: targetBearingHelp,
      }),
      ...lateralLead().fields,
    ],
  },
  {
    title: "Wind",
    icon: "bi-wind",
    fields: [
      number("wd", "Wind direction", 0, {
        min: 0, max: 360, step: 0.1, unit: "°", help: windDirectionHelp,
      }),
      number("ws", "Wind speed", 0, {
        min: 0, max: 100, step: 0.1, unit: "mph",
      }),
      number("wy", "Vertical wind", 0, {
        min: -100, max: 100, step: 0.1, unit: "mph", advanced: true,
      }),
    ],
  },
  {
    title: "Engagement Zone",
    icon: "bi-bullseye",
    fields: [
      select("ezTargetType", "Target shape", "uspsa", [
        { value: "uspsa", label: "Standard USPSA" },
        { value: "custom", label: "Custom rectangle" },
      ], {
        wide: true,
        help: "The standard target uses the current 450 × 750 mm USPSA silhouette. A custom target is a centered rectangle with the dimensions entered below.",
      }),
      number("ezTargetWidth", "Custom target width", 18, {
        min: 0.1, max: 1000, step: 0.1, unit: "in", required: true,
        showWhen: { name: "ezTargetType", value: "custom" },
      }),
      number("ezTargetHeight", "Custom target height", 30, {
        min: 0.1, max: 1000, step: 0.1, unit: "in", required: true,
        showWhen: { name: "ezTargetType", value: "custom" },
      }),
      number("ezVelocityEs", "Velocity extreme spread", 40, {
        min: 0, max: 1000, step: 0.1, unit: "ft/s", unitGroup: "velocitySpread",
        help: "Extreme spread (ES) is your fastest measured shot minus your slowest. For example, 2,780 − 2,750 = 30 ft/s ES. Enter that difference. Engagement Zone models it as the full 95% velocity span and uses ES ÷ 4 as the standard deviation. ES depends on how many shots you fire, so use a representative chronograph string.",
      }),
      number("ezRifleAccuracy", "Rifle accuracy (group size)", 0.5, {
        min: 0, max: 20, step: 0.01, unit: "MOA", unitGroup: "dispersionAngle",
        help: "The full angular group diameter. Following the reference convention, horizontal and vertical dispersion each use a standard deviation equal to one quarter of this value.",
      }),
      number("ezRangeUncertainty", "Range uncertainty (±)", 1, {
        min: 0, max: TRAJECTORY_MAX_RANGE_YARDS, step: 0.1, unit: "yd",
        help: engagementUncertaintyHelp,
      }),
      number("ezWindSpeedUncertainty", "Wind speed uncertainty (±)", 1, {
        min: 0, max: 100, step: 0.1, unit: "mph",
        help: engagementUncertaintyHelp,
      }),
      number("ezWindDirectionUncertainty", "Wind direction uncertainty (±)", 0, {
        min: 0, max: 180, step: 0.1, unit: "°",
        help: engagementUncertaintyHelp,
      }),
      checkbox(
        "ezTransonicUncertainty",
        "Include generic transonic uncertainty estimate",
        true,
        {
          help: "Adds a generic directional uncertainty allowance while the bullet slows from Mach 1.2 to Mach 1.0. This is a general estimate, not a prediction of this bullet's stability. Uncheck it to exclude the allowance.",
        },
      ),
      number("ezTransonicSeverity", "Transonic instability severity",
        ENGAGEMENT_ZONE_DEFAULT_TRANSONIC_SEVERITY_PERCENT, {
        min: 0, max: 100, step: 1, unit: "%",
        showWhenAny: [{ name: "ezTransonicUncertainty", checked: true }],
        help: "Controls the generic directional disturbance only while the bullet is between Mach 1.2 and Mach 1.0. The effect scales linearly: 0% adds none, 50% (the general starting estimate) permits up to ±5 m/s per sampled direction component, and 100% permits up to ±10 m/s. The bullet's speed is preserved. Higher values allow more dispersion, but do not guarantee a lower hit rate in any one simulation. This is not a prediction of this bullet's stability.",
      }),
    ],
  },
  {
    title: "Sight adjustment",
    icon: "bi-sliders",
    advanced: true,
    fields: [
      number("el", "Dialed elevation", 0, {
        min: -300, max: 300, step: 0.01, unit: "MOA", help: dialedElevationHelp,
      }),
      number("az", "Dialed windage", 0, {
        min: -300, max: 300, step: 0.01, unit: "MOA", help: dialedWindageHelp,
      }),
      hidden("elc", "on"),
      hidden("azc", "on"),
    ],
  },
  {
    title: "Angles and location",
    icon: "bi-compass",
    fields: [
      number("la", "Shot inclination angle", 0, {
        min: -90, max: 90, step: 0.1, unit: "°", help: shotAngleHelp,
      }),
      number("ca", "Cant angle", 0, {
        min: -90, max: 90, step: 0.1, unit: "°", advanced: true,
      }),
      number("lat", "Latitude", "", {
        min: -90, max: 90, step: 0.01, unit: "°",
        preserveBlank: true,
        help: "Optional signed latitude. Entering a value—including 0° at the equator—automatically enables Coriolis calculations using the target bearing as firing azimuth.",
      }),
    ],
  },
  {
    title: "Output settings",
    icon: "bi-graph-up",
    fields: [
      hidden("un", 3),
      select("outputMode", "Output", "angular", outputModes, { bottomBar: true }),
    ],
  },
  atmosphere({ supportsMvTemperature: true }),
];

const trajectorySections = [
  {
    title: "Projectile",
    icon: "bi-bullseye",
    fields: [
      temperatureVelocityList("mvtb", "MV-Temp table", "59:3000", {
        wide: true, required: true, maxRows: 32,
        help: mvTemperatureTableHelp,
      }),
      number("xc", "Chronograph distance", 0, {
        min: 0, max: 100, step: 0.1, unit: "ft", advanced: true,
        help: "Distance from the muzzle at which every velocity in the MV-Temp table was measured. Enter zero when the table contains true muzzle velocities.",
      }),
      number("bc", "Ballistic coefficient", 0.5, {
        min: 0.01, max: 2, step: 0.001, unit: "G1", unitField: "df",
        help: ballisticCoefficientHelp,
      }),
      hidden("df", 1),
      number("wt", "Bullet weight", 220, { min: 5, max: 5000, step: 0.1, unit: "gr" }),
      ...velocityBandFields(),
    ],
  },
  bcEstimateGeometry({ supportsSpinDrift: true }),
  {
    title: "Sight adjustment",
    icon: "bi-crosshair",
    fields: [
      number("sh", "Sight height", 1.5, { min: -100, max: 100, step: 0.01, unit: "in" }),
      number("so", "Sight offset", 0, {
        min: -100, max: 100, step: 0.01, unit: "in", advanced: true,
      }),
      number("el", "Dialed elevation", 0, {
        min: -300, max: 300, step: 0.01, unit: "MOA", advanced: true,
        help: dialedElevationHelp,
      }),
      number("az", "Dialed windage", 0, {
        min: -300, max: 300, step: 0.01, unit: "MOA", advanced: true,
        help: dialedWindageHelp,
      }),
      number("rz", "Zero range", 100, { min: 10, max: TRAJECTORY_MAX_RANGE_YARDS, step: "any", unit: "yd" }),
      number("zy", "Zero shift elevation", 0, {
        min: -100, max: 100, step: 0.01, unit: "in", advanced: true,
        help: zeroShiftElevationHelp,
      }),
      number("zz", "Zero shift windage", 0, {
        min: -100, max: 100, step: 0.01, unit: "in", advanced: true,
        help: zeroShiftWindageHelp,
      }),
    ],
  },
  {
    title: "Conditions",
    icon: "bi-wind",
    advanced: true,
    fields: [
      number("wd", "Wind direction", 0, {
        min: 0, max: 360, step: 0.1, unit: "°", help: windDirectionHelp,
      }),
      number("ws", "Wind speed", 0, { min: 0, max: 100, step: 0.1, unit: "mph" }),
      number("wy", "Vertical wind", 0, { min: -100, max: 100, step: 0.1, unit: "mph" }),
    ],
  },
  lateralLead({ advanced: true }),
  {
    title: "Angles",
    icon: "bi-compass",
    advanced: true,
    fields: [
      number("la", "Shot inclination angle", 0, {
        min: -90, max: 90, step: 0.1, unit: "°", help: shotAngleHelp,
      }),
      number("ca", "Cant angle", 0, { min: -90, max: 90, step: 0.1, unit: "°" }),
    ],
  },
  trajectoryRange(),
  trajectoryCorrections({ advanced: true }),
  atmosphere({ supportsMvTemperature: true }),
];

const rangeTableSections = [
  {
    title: "Projectile",
    icon: "bi-bullseye",
    fields: [
      number("mv", "Reference muzzle velocity", 2900, {
        min: 500, max: 5000, step: 0.1, unit: "ft/s",
      }),
      number("bc", "Ballistic coefficient", 0.5, {
        min: 0.01, max: 2, step: 0.001, unit: "G1", unitField: "df",
        help: ballisticCoefficientHelp,
      }),
      hidden("df", 1),
      number("wt", "Bullet weight", 220, { min: 5, max: 5000, step: 0.1, unit: "gr" }),
      ...velocityBandFields(),
      hidden("xc", 0),
    ],
  },
  bcEstimateGeometry({ supportsSpinDrift: true }),
  {
    title: "Temperature series and atmosphere",
    icon: "bi-thermometer-half",
    inputPresets: {
      label: "Load sea-level atmosphere",
      help: "ICAO (International Civil Aviation Organization) is the modern dry-air sea-level standard: 59 °F, 29.92 inHg, and 0% relative humidity. ASM (Army Standard Metro) is the older ballistic standard: 59 °F, 29.53 inHg, and 78% relative humidity. On the Range Table, a preset fills station pressure and humidity while leaving the temperature series unchanged. It does not convert the entered ballistic coefficient between reference standards.",
      options: rangeTableAtmospherePresets,
    },
    fields: [
      mvTemperatureSensitivity(),
      numberList("temps", "Air / ammunition temperatures", "5, 23, 41, 50, 59, 68, 77, 86, 95", {
        min: -40, max: 140, minItems: 3, maxItems: 12, unit: "°F", wide: true,
        help: "Enter 3–12 temperatures separated by commas, spaces, or new lines. Values are sorted from coldest to hottest.",
      }),
      number("pr", "Station pressure", ICAO_PRESSURE_INHG, {
        min: 15, max: 40, step: 0.01, unit: "in Hg",
        help: "Enter the actual uncorrected pressure at the firing location. Altitude is not needed when station pressure is known; do not use sea-level-corrected weather pressure.",
      }),
      number("hu", "Relative humidity", 0, { min: 0, max: 100, step: 0.1, unit: "%" }),
    ],
  },
  {
    title: "Sight adjustment",
    icon: "bi-crosshair",
    fields: [
      number("sh", "Sight height", 1.5, { min: -100, max: 100, step: 0.01, unit: "in" }),
      number("so", "Sight offset", 0, {
        min: -100, max: 100, step: 0.01, unit: "in", advanced: true,
      }),
      number("el", "Dialed elevation", 0, {
        min: -300, max: 300, step: 0.01, unit: "MOA", advanced: true,
        help: dialedElevationHelp,
      }),
      number("az", "Dialed windage", 0, {
        min: -300, max: 300, step: 0.01, unit: "MOA", advanced: true,
        help: dialedWindageHelp,
      }),
      number("rz", "Zero range", 100, {
        min: 10, max: TRAJECTORY_MAX_RANGE_YARDS, step: "any", unit: "yd",
      }),
      number("zy", "Zero shift elevation", 0, {
        min: -100, max: 100, step: 0.01, unit: "in", advanced: true,
        help: zeroShiftElevationHelp,
      }),
      number("zz", "Zero shift windage", 0, {
        min: -100, max: 100, step: 0.01, unit: "in", advanced: true,
        help: zeroShiftWindageHelp,
      }),
    ],
  },
  {
    title: "Conditions",
    icon: "bi-wind",
    fields: [
      number("wd", "Wind direction", 0, {
        min: 0, max: 360, step: 0.1, unit: "°", help: windDirectionHelp,
      }),
      number("ws", "Wind speed", 0, { min: 0, max: 100, step: 0.1, unit: "mph" }),
      number("wy", "Vertical wind", 0, {
        min: -100, max: 100, step: 0.1, unit: "mph", advanced: true,
      }),
    ],
  },
  lateralLead(),
  {
    title: "Angles",
    icon: "bi-compass",
    advanced: true,
    fields: [
      number("la", "Shot inclination angle", 0, {
        min: -90, max: 90, step: 0.1, unit: "°", help: shotAngleHelp,
      }),
      number("ca", "Cant angle", 0, { min: -90, max: 90, step: 0.1, unit: "°" }),
    ],
  },
  trajectoryRange(),
  trajectoryCorrections(),
];

const timeZones = Array.from({ length: 25 }, (_, index) => {
  const value = index - 12;
  return { value: String(value), label: `UTC${value >= 0 ? "+" : ""}${value}` };
});

export const calculators = [
  {
    slug: "trajectory",
    title: "Trajectory",
    shortTitle: "Trajectory",
    category: "Trajectory",
    icon: "bi-graph-up",
    module: "trajectory",
    mvTemperatureMode: "shot",
    populateGunParameters: trajectoryPopulateGunParameters,
    advancedMode: true,
    sourceControlledResultUnits: trajectorySourceControlledResultUnits,
    description: "Solve a point-mass trajectory with focused defaults and optional advanced wind, angle, geometry, and rotation inputs.",
    sections: trajectorySections,
  },
  {
    slug: "solution",
    title: "Solution",
    shortTitle: "Solution",
    category: "Trajectory",
    icon: "bi-crosshair2",
    module: "trajectory",
    resultMode: "solution",
    mvTemperatureMode: "shot",
    advancedMode: true,
    requiresGun: true,
    gunParameters: trajectoryGunParameters,
    derivedGunParameters: derivedMuzzleVelocityParameter,
    sourceControlledResultUnits: trajectorySourceControlledResultUnits,
    description: "Calculate one target solution from a loaded gun, absolute target and wind bearings, current conditions, and optional lateral movement.",
    sections: solutionSections,
  },
  {
    slug: "range-table",
    title: "Temperature Range Table",
    shortTitle: "Range Table",
    category: "Range",
    icon: "bi-table",
    module: "trajectory",
    resultMode: "range-table",
    mvTemperatureMode: "series",
    advancedMode: true,
    requiresGun: true,
    gunParameters: trajectoryGunParameters,
    derivedGunParameters: derivedMuzzleVelocityParameter,
    sourceControlledResultUnits: trajectorySourceControlledResultUnits,
    description: "Create a temperature-indexed drop table with adjusted muzzle velocities plus low, middle, and high temperature samples for configured wind and lateral lead.",
    sections: rangeTableSections,
  },
  {
    slug: "point-blank-range",
    title: "Point Blank Range",
    shortTitle: "Point Blank Range",
    category: "Range",
    icon: "bi-arrows-expand",
    module: "point-blank-range",
    mvTemperatureMode: "icao",
    advancedMode: true,
    requiresGun: true,
    gunParameters: pointBlankGunParameters,
    derivedGunParameters: derivedMuzzleVelocityParameter,
    description: "Find maximum point blank range, zero, and retained energy for a vital-zone radius.",
    sections: [
      {
        title: "Projectile", icon: "bi-bullseye", fields: [
          number("mv", "Muzzle velocity", 3000, { min: 500, max: 5000, step: 0.1, unit: "ft/s" }),
          number("wt", "Bullet weight", 220, { min: 10, max: 5000, step: 0.1, unit: "gr" }),
          number("bc", "Ballistic coefficient", 0.5, {
            min: 0.05, max: 2, step: 0.001, unit: "G1", unitField: "df",
            help: ballisticCoefficientHelp,
          }),
          hidden("df", 1),
          ...velocityBandFields(),
        ],
      },
      bcEstimateGeometry(),
      {
        title: "Zero geometry", icon: "bi-crosshair", fields: [
          number("sh", "Sight height", 1.5, { min: 0, max: 100, step: 0.01, unit: "in" }),
          number("vz", "Vital-zone radius", 5, { min: 0.1, max: 100, step: 0.1, unit: "in" }),
        ],
      },
      atmosphere(),
    ],
  },
  {
    slug: "maximum-distance",
    title: "Maximum Distance",
    shortTitle: "Maximum Distance",
    category: "Range",
    icon: "bi-arrows-angle-expand",
    module: "maximum-distance",
    mvTemperatureMode: "icao",
    advancedMode: true,
    requiresGun: true,
    gunParameters: maximumDistanceGunParameters,
    derivedGunParameters: derivedMuzzleVelocityParameter,
    description: "Estimate maximum range, elevation, height, flight time, and terminal projectile state.",
    sections: [
      {
        title: "Projectile", icon: "bi-bullseye", fields: [
          number("mv", "Muzzle velocity", 3000, { min: 500, max: 5000, step: 0.1, unit: "ft/s" }),
          number("bc", "Ballistic coefficient", 0.5, {
            min: 0.05, max: 2, step: 0.001, unit: "G1", unitField: "df",
            help: ballisticCoefficientHelp,
          }),
          hidden("df", 1),
          number("wt", "Bullet weight", 220, { min: 10, max: 5000, step: 0.1, unit: "gr" }),
          ...velocityBandFields(),
        ],
      },
      bcEstimateGeometry(),
      atmosphere(),
    ],
  },
  {
    slug: "ballistic-coefficient-velocity",
    title: "Ballistic Coefficient — Velocity",
    shortTitle: "BC from Velocity",
    category: "BC & Drag",
    icon: "bi-speedometer2",
    module: "ballistic-coefficient-velocity",
    description: "Calculate ballistic coefficient from near and far velocities measured across a known distance.",
    sections: [
      {
        title: "Measurement", icon: "bi-rulers", fields: [
          number("nv", "Near velocity", 3000, { min: 600, max: 4500, step: 0.1, unit: "ft/s" }),
          number("fv", "Far velocity", 2850, { min: 500, max: 4400, step: 0.1, unit: "ft/s" }),
          number("xc", "Distance", 100, { min: 1, max: 2500 / 0.3048, step: "any", unit: "ft" }),
          select("df", "Drag function", 1, dragFunctions),
        ],
      },
      atmosphere(),
    ],
  },
  {
    slug: "ballistic-coefficient-time",
    title: "Ballistic Coefficient — Time",
    shortTitle: "BC from Time",
    category: "BC & Drag",
    icon: "bi-stopwatch",
    module: "ballistic-coefficient-time",
    description: "Calculate ballistic coefficient and far velocity from near velocity, distance, and flight time.",
    sections: [
      {
        title: "Measurement", icon: "bi-rulers", fields: [
          number("nv", "Near velocity", 3000, { min: 600, max: 4500, step: 0.1, unit: "ft/s" }),
          number("tm", "Flight time", 0.103, { min: 0.000001, max: 20, step: 0.000001, unit: "s" }),
          number("xc", "Distance", 300, { min: 1, max: 2500 / 0.3048, step: "any", unit: "ft" }),
          select("df", "Drag function", 1, dragFunctions),
        ],
      },
      atmosphere(),
    ],
  },
  {
    slug: "drag-and-twist",
    title: "Bullet Drag and Twist",
    shortTitle: "Drag & Twist",
    category: "BC & Drag",
    icon: "bi-tornado",
    module: "drag-and-twist",
    description: "Estimate drag components, form factor, BC, required twist, stability, and RPM from bullet geometry.",
    sections: [
      {
        title: "Bullet geometry", icon: "bi-bounding-box", fields: [
          number("dia", "Caliber", 0.308, { min: 0.1, max: 2, step: 0.001, unit: "in" }),
          number("tln", "Total length", 1.464, { min: 0.1, max: 10, step: 0.001, unit: "in" }),
          number("nsl", "Nose length", 0.740, { min: 0.1, max: 6, step: 0.001, unit: "in" }),
          number("dmp", "Meplat diameter", 0.062, { min: 0, max: 1, step: 0.001, unit: "in" }),
          number("rad", "Nose radius", 15, { min: 1, max: 1000, step: 0.1, unit: "cal" }),
          select("nst", "Nose type", 1, [
            { value: "1", label: "Secant" }, { value: "2", label: "Tangent" }, { value: "3", label: "Conical" },
          ]),
          number("btl", "Boattail length", 0.180, { min: 0, max: 2, step: 0.001, unit: "in" }),
          number("dba", "Base diameter", 0.251, { min: 0.1, max: 1, step: 0.001, unit: "in" }),
          number("dbd", "Rotating-band diameter", 0.308, { min: 0.1, max: 1.2, step: 0.001, unit: "in" }),
        ],
      },
      {
        title: "Mass and stability", icon: "bi-arrow-repeat", fields: [
          number("wgt", "Bullet weight", 210, { min: 10, max: 5000, step: 0.1, unit: "gr" }),
          number("spg", "Specific gravity", 10.7, { min: 2, max: 20, step: 0.1 }),
          select("dgf", "Reference drag function", 1, dragFunctions),
          number("twt", "Barrel twist", 10, { min: 2, max: 100, step: 0.1, unit: "in/turn" }),
          checkbox("shc", "Show individual drag components"),
        ],
      },
      atmosphere(),
    ],
  },
  {
    slug: "drag-function-conversion",
    title: "Drag Function Conversion",
    shortTitle: "Drag Conversion",
    category: "BC & Drag",
    icon: "bi-arrow-left-right",
    module: "drag-function-conversion",
    description: "Convert a ballistic coefficient between reference drag functions at a representative velocity.",
    sections: [
      {
        title: "Conversion", icon: "bi-arrow-left-right", fields: [
          number("bc", "Ballistic coefficient", 0.5, {
            min: 0.01, max: 2, step: 0.001, unit: "G1", unitField: "df",
          }),
          hidden("df", 1),
          select("ndf", "New drag function", 5, dragFunctions),
          number("vel", "Representative velocity", 3000, { min: 500, max: 4800, step: 0.1, unit: "ft/s" }),
        ],
      },
      {
        title: "Bullet", icon: "bi-bullseye", fields: [
          number("cal", "Caliber", 0.308, { min: 0.1, max: 2, step: 0.001, unit: "in" }),
          number("wt", "Bullet weight", 220, { min: 5, max: 15000, step: 0.1, unit: "gr" }),
        ],
      },
    ],
  },
  {
    slug: "sunrise-sunset",
    title: "Sunrise and Sunset",
    shortTitle: "Sunrise & Sunset",
    category: "Utility",
    icon: "bi-sunrise",
    module: "sunrise-sunset",
    description: "Calculate sunrise, sunset, and day length for a date, location, time zone, and DST setting.",
    sections: [
      {
        title: "Date", icon: "bi-calendar3", fields: [
          number("dy", "Day", 1, { min: 1, max: 31, step: 1 }),
          number("mo", "Month", 1, { min: 1, max: 12, step: 1 }),
          number("yr", "Year", 1997, { min: 1801, max: 2099, step: 1 }),
        ],
      },
      {
        title: "Location", icon: "bi-geo-alt", fields: [
          number("lt_d", "Latitude degrees", 32, { min: -90, max: 90, step: 1, unit: "°" }),
          number("lt_m", "Latitude minutes", 16, { min: 0, max: 59, step: 1, unit: "′" }),
          number("lt_s", "Latitude seconds", 20.748, { min: 0, max: 59.999, step: 0.001, unit: "″" }),
          number("ln_d", "Longitude degrees", -106, { min: -180, max: 180, step: 1, unit: "°" }),
          number("ln_m", "Longitude minutes", 46, { min: 0, max: 59, step: 1, unit: "′" }),
          number("ln_s", "Longitude seconds", 50.52, { min: 0, max: 59.999, step: 0.001, unit: "″" }),
          select("tz", "Time zone", -7, timeZones),
          checkbox("dst", "Apply daylight-saving correction", true),
        ],
      },
    ],
  },
];

export const calculatorGroups = calculators.reduce((groups, calculator) => {
  const group = groups.find((item) => item.title === calculator.category);
  if (group) group.calculators.push(calculator);
  else groups.push({ title: calculator.category, calculators: [calculator] });
  return groups;
}, []);

export function getCalculator(slug) {
  const canonicalSlug = slug === "trajectory-simplified" ? "trajectory" : slug;
  return calculators.find((calculator) => calculator.slug === canonicalSlug);
}

export function calculatorDefaults(calculator) {
  const defaults = {};
  for (const section of calculator.sections) {
    for (const field of section.fields) {
      defaults[field.name] = field.type === "checkbox" ? field.checked : field.value;
    }
  }
  return defaults;
}

export function gunDefaults() {
  return calculatorDefaults(gunEditor);
}

export function findGunField(name) {
  return gunSections.flatMap((section) => section.fields)
    .find((field) => field.name === name);
}

export function calculatorUsesGunField(calculator, name) {
  return Boolean(calculator?.requiresGun && (
    calculator.gunParameters?.includes(name)
      || calculator.derivedGunParameters?.includes(name)
  ));
}
